import {
  getRrwebIframe,
  pickInitialTooltipPlacement,
} from '@carboncopyco/mimesis-shared';
import {
  type FrameType,
  RelativePositionAlignmentEnums,
  RelativePositionTypeEnums,
  type TourFrame,
  type TourStep,
  TourStepAddDirection,
  tooltipDefaultConfig,
} from '@carboncopyco/types';
import type { RefObject } from 'react';
import { getNewTooltipConfig } from './tooltipConfig';

interface IGetStepConfigToInheritFrom {
  activeFrame: TourFrame;
  playableFrames: TourFrame[];
  addStepReferenceStepId?: TourStep['stepId'];
  addStepDirection?: TourStepAddDirection;
}

export const getTooltipPlacement = (
  step: TourStep,
  frameType: FrameType,
  rootElement?: RefObject<HTMLDivElement>
) => {
  const iframe = getRrwebIframe(rootElement?.current);
  if (!iframe || !iframe?.contentWindow || !iframe?.contentDocument) {
    return {
      relation: RelativePositionTypeEnums.page,
      alignment: RelativePositionAlignmentEnums.middleCenter,
    };
  }

  if (!step.xpath && !step.elementPosition) {
    return {
      relation: RelativePositionTypeEnums.page,
      alignment: RelativePositionAlignmentEnums.middleCenter,
    };
  }

  return pickInitialTooltipPlacement({
    xpath: step.xpath,
    elementPosition: step.elementPosition,
    frameType,
    doc: iframe.contentDocument,
  });
};

const getStepConfigToInheritFrom = ({
  activeFrame,
  playableFrames,
  addStepReferenceStepId,
  addStepDirection,
}: IGetStepConfigToInheritFrom) => {
  const framesWithSteps = playableFrames.filter(
    (frame: TourFrame) => frame.steps.length > 0
  );

  const currentActiveFrameIndex = framesWithSteps.findIndex(
    (frame: TourFrame) => frame.id === activeFrame.id
  );

  // Adding steps to a frame without steps
  if (activeFrame?.steps.length === 0) {
    const selectedFrame = playableFrames.find(
      (frame) => frame.id === activeFrame.id
    );
    for (let i = selectedFrame.frameIndex - 1; i >= 0; i--) {
      if (playableFrames[i]?.isActive && playableFrames[i].steps.length > 0) {
        return playableFrames[i].steps[playableFrames[i].steps.length - 1]
          .config;
      }
    }
    return null;
  }

  // Adding step above or below another step
  if (addStepReferenceStepId && addStepDirection) {
    const referenceStepIndex = activeFrame.steps.findIndex(
      (step) => step.stepId === addStepReferenceStepId
    );

    let stepIndexToInheritConfigFrom =
      addStepDirection === TourStepAddDirection.below
        ? referenceStepIndex
        : referenceStepIndex - 1;

    // Adding step above the first step of a frame
    if (
      addStepDirection === TourStepAddDirection.above &&
      referenceStepIndex === 0
    ) {
      if (currentActiveFrameIndex !== 0) {
        const previousFrame = framesWithSteps[currentActiveFrameIndex - 1];
        return previousFrame.steps[previousFrame.steps.length - 1]?.config;
      }

      stepIndexToInheritConfigFrom = 0;
    }

    return activeFrame.steps[stepIndexToInheritConfigFrom]?.config;
  }

  // Adding step to the last frame
  if (currentActiveFrameIndex === framesWithSteps.length - 1) {
    if (activeFrame.steps.length === 1) {
      const previousFrame =
        framesWithSteps[
          currentActiveFrameIndex === 0 ? 0 : currentActiveFrameIndex - 1
        ];

      return previousFrame.steps[previousFrame.steps.length - 1].config;
    }

    return activeFrame.steps?.[activeFrame.steps.length - 2]?.config;
  }

  return activeFrame.steps[activeFrame.steps.length - 1]?.config;
};

export const getStepConfig = ({
  step,
  activeFrame,
  playableFrames,
  addStepReferenceStepId,
  addStepDirection,
  shouldInheritConfig,
  frameType,
  rootElement,
}: IGetStepConfigToInheritFrom & {
  step: TourStep;
  shouldInheritConfig: boolean;
  frameType: FrameType;
  rootElement: RefObject<HTMLDivElement>;
}) => {
  const tooltipPlacement = getTooltipPlacement(step, frameType, rootElement);
  const stepConfigToInheritFrom = shouldInheritConfig
    ? getStepConfigToInheritFrom({
        activeFrame,
        playableFrames,
        addStepReferenceStepId,
        addStepDirection,
      })
    : step.config;

  if (stepConfigToInheritFrom) {
    return {
      tooltip: getNewTooltipConfig({
        config: {
          ...stepConfigToInheritFrom.tooltip,
          balloon: {
            ...stepConfigToInheritFrom.tooltip.balloon,
            layout: tooltipPlacement,
          },
        },
      }),
    };
  }

  return {
    tooltip: {
      ...tooltipDefaultConfig,
      balloon: {
        ...tooltipDefaultConfig.balloon,
        layout: tooltipPlacement,
      },
    },
  };
};
